import { MenuItem, MenuList } from '@chakra-ui/react';
import { ReactNode } from 'react';

import { font, neutral, radius, shadow, text as textToken } from 'styles/revampTokens';

/**
 * Shared shell for every icon+label dropdown menu in the V2 UI — the
 * dimension row's Rename/Duplicate/Delete menu, the Dimensions header's
 * Create menu, and the block card's Edit/Duplicate/Select/Delete menu.
 *
 * Each of these started out hand-built with its own copy of the same
 * padding/radius/border/shadow values, and they drifted badly: the block
 * card's menu was 200px wide with 16px rows, 16px corners, a 0.25-alpha
 * shadow and grey lines showing between items, while the dimension menu next
 * to it was 153px with 9/11px rows and a hairline border. Building them all
 * from one component makes that drift structurally impossible.
 *
 * Lives in `components/` rather than inside either feature folder so neither
 * module has to import from the other.
 */

export interface ActionMenuItem {
  key: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  /** Rare — only a destructive row (Delete) needs this. */
  color?: string;
  isDisabled?: boolean;
}

export const ActionMenuList = ({
  items,
  minW = '153px',
}: {
  items: ActionMenuItem[];
  minW?: string;
}) => (
  <MenuList
    p={0}
    display='flex'
    flexDirection='column'
    bg={neutral.surface}
    border={`1px solid ${neutral.border}`}
    borderRadius={radius.overlay}
    overflow='hidden'
    boxShadow={shadow.overlay}
    minW={minW}
    fontSize='13px'
    zIndex={9999}
  >
    {items.map((item) => (
      <MenuItem
        key={item.key}
        isDisabled={item.isDisabled}
        onClick={item.onSelect}
        bg={neutral.surface}
        borderRadius='0 !important'
        gap='8px'
        p='9px 11px'
        fontFamily={font.body}
        color={item.color ?? textToken.primary}
        _hover={{ bg: neutral.surfaceHover }}
        _focus={{ bg: neutral.surface }}
      >
        {item.icon}
        {item.label}
      </MenuItem>
    ))}
  </MenuList>
);
